import { Color, Spacing } from '@persona-id/design-tokens';
import { CheckIcon, XIcon } from '@persona-id/design-tokens/icons';
import { kebabCase, snakeCase, startCase } from 'lodash/fp';
import { Controller, FieldErrors, useFieldArray, UseFormReturn } from 'react-hook-form';
import styled from 'styled-components';

import { FieldWrapper } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/shared';
import ExtractionPanelListBox from 'dashboard/components/ExtractionPanel/ExtractionPanelListBox';
import {
  ExtractionPanelForm,
  UnguidedAiExtractionPreference,
} from 'dashboard/components/ExtractionPanel/util';
import { ReactCreatableSelect } from 'dashboard/components/ReactSelect';
import {
  DEPRECATED_PRESET_EXTRACTORS,
  ExtractAction,
  ExtractionFieldType,
  ExtractionRequest,
  ExtractionType,
  ExtractorType,
  PRESET_EXTRACTOR_TO_DISPLAY_NAME,
  PresetExtractor,
  RegexExtractor,
  TextExtractor,
  UnguidedAiExtractionParams,
  UnguidedAiExtractionParamsPropertyValue,
  UnguidedExtractionRequest,
} from 'dashboard/models/document';
import { ErrorText } from 'design-system/components/FormControl';
import Input from 'design-system/components/Input';
import Radio from 'design-system/components/Radio';
import RadioGroup from 'design-system/components/RadioGroup';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { Label, Paragraph } from 'design-system/components/Text';
import TextArea from 'design-system/components/TextArea';
import Tooltip from 'design-system/components/Tooltip';
import { assertNever } from 'lib/utils/types';

const EXTRACT_ACTION_TO_DISPLAY_NAME: Record<ExtractAction, string> = {
  [ExtractAction.Accept]: 'Accept match',
  [ExtractAction.Reject]: 'Discard match',
};

const EXTRACTION_PREFERENCE_TO_DESCRIPTION: Record<UnguidedAiExtractionPreference, string> = {
  [UnguidedAiExtractionPreference.BestMatch]:
    'Returns the single best extraction that meets the specified criteria, based on the highest confidence score among all extracted results.',
  [UnguidedAiExtractionPreference.AllMatches]:
    'Returns all unique extractions that meet the specified criteria, providing a wider range of potential results.',
};

const StyledLabel = styled(Label)`
  margin-bottom: ${Spacing[2]};
  display: inline-block;
`;

const ExtractorEntry = styled.div`
  display: flex;
  align-items: center;
`;

// TODO: [Documents] Migrate to PDS component for underline tooltip indicators when available
const TooltipIndicatorWrapper = styled.div`
  width: fit-content;
  text-decoration: underline;
  text-decoration-style: dotted;
`;

const TooltipParagraph = styled(Paragraph)`
  width: 200px;
`;

const FieldErrorText = ({ message }: { message?: string }) =>
  message ? (
    <>
      <Spacer height={0.5} />
      <ErrorText>{message}</ErrorText>
    </>
  ) : null;

export const unguidedExtractionRequestToExtractionFormState = (
  extractionRequest: UnguidedExtractionRequest,
): Partial<ExtractionPanelForm> => {
  const unguidedBase = {
    fieldName: startCase(extractionRequest['field-name']),
    extractionType: extractionRequest['extraction-type'],
    extractorType: extractionRequest['extractor-type'],
  };
  switch (extractionRequest['extractor-type']) {
    case ExtractorType.Ai:
      return {
        ...unguidedBase,
        aiFieldType: extractionRequest['extraction-params'].title,
        extractionKeys: Object.keys(extractionRequest['extraction-params'].properties).map(
          (extractionKey) => snakeCase(extractionKey),
        ),
        extractionPreference:
          Object.values(extractionRequest['extraction-params'].properties).length === 0 ||
          Object.values(extractionRequest['extraction-params'].properties)[0].type === 'string'
            ? UnguidedAiExtractionPreference.BestMatch
            : UnguidedAiExtractionPreference.AllMatches,
        additionalInstructions:
          extractionRequest['extraction-params'].properties[
            kebabCase(extractionRequest['primary-property-key'])
          ]?.description,
      };
    case ExtractorType.Text:
      return {
        ...unguidedBase,
        textExtractors: extractionRequest['text-extractors'],
      };
    case ExtractorType.Regex:
      return {
        ...unguidedBase,
        regexExtractors: extractionRequest['regex-extractors'],
      };
    case ExtractorType.KeyValuePair:
      return {
        ...unguidedBase,
        searchKeys: extractionRequest['search-keys'],
      };
    case ExtractorType.Preset:
      return {
        ...unguidedBase,
        presetExtractor: extractionRequest['preset-extractor'],
      };
    default:
      return unguidedBase;
  }
};

export const formStateToUnguidedExtractionRequest = (
  data: ExtractionPanelForm,
  oldExtractionRequest: ExtractionRequest | null,
): ExtractionRequest => {
  const keepOldExtractionRequest =
    oldExtractionRequest?.['extraction-type'] === ExtractionType.Unguided &&
    oldExtractionRequest['extractor-type'] === data.extractorType;

  switch (data.extractorType) {
    case ExtractorType.Ai: {
      const propertyValue: UnguidedAiExtractionParamsPropertyValue =
        data.extractionPreference === UnguidedAiExtractionPreference.BestMatch
          ? { type: 'string' }
          : { type: 'array', items: { type: 'string' } };

      const properties: Record<string, UnguidedAiExtractionParamsPropertyValue> = {};

      // TODO: Clean this up after Docs GA Product Readiness is fully launched and `extractionKeys` are fully deprecated
      const primaryPropertyKey = snakeCase(data.fieldName);
      properties[kebabCase(primaryPropertyKey)] = propertyValue;

      const extractionParams: UnguidedAiExtractionParams = {
        type: 'object',
        // TODO: Clean-up `title` attribute after completely deprecated from the back-end
        //       For the time being, default to `string` since it doesn't matter.
        title: 'string',
        properties: properties,
      };
      if (data.additionalInstructions) {
        extractionParams.properties[kebabCase(primaryPropertyKey)] = {
          ...extractionParams.properties[kebabCase(primaryPropertyKey)],
          description: data.additionalInstructions,
        };
      }

      return {
        ...(keepOldExtractionRequest ? oldExtractionRequest : {}),
        version: '2.0',
        'extraction-type': ExtractionType.Unguided,
        'field-type': ExtractionFieldType.String,
        'field-name': snakeCase(data.fieldName),
        'extractor-type': data.extractorType,
        'extraction-params': extractionParams,
        'primary-property-key': primaryPropertyKey,
      };
    }
    case ExtractorType.Text:
      return {
        ...(keepOldExtractionRequest ? oldExtractionRequest : {}),
        version: '2.0',
        'extraction-type': ExtractionType.Unguided,
        'field-type': ExtractionFieldType.String,
        'field-name': snakeCase(data.fieldName),
        'extractor-type': data.extractorType,
        'text-extractors': data.textExtractors as TextExtractor[],
      };
    case ExtractorType.Regex:
      return {
        ...(keepOldExtractionRequest ? oldExtractionRequest : {}),
        version: '2.0',
        'extraction-type': ExtractionType.Unguided,
        'field-type': ExtractionFieldType.String,
        'field-name': snakeCase(data.fieldName),
        'extractor-type': data.extractorType,
        'regex-extractors': data.regexExtractors as RegexExtractor[],
      };
    case ExtractorType.KeyValuePair:
      return {
        ...(keepOldExtractionRequest ? oldExtractionRequest : {}),
        version: '2.0',
        'extraction-type': ExtractionType.Unguided,
        'field-type': ExtractionFieldType.String,
        'field-name': snakeCase(data.fieldName),
        'extractor-type': data.extractorType,
        'search-keys': data.searchKeys,
      };
    case ExtractorType.Preset:
      return {
        ...(keepOldExtractionRequest ? oldExtractionRequest : {}),
        version: '2.0',
        'extraction-type': ExtractionType.Unguided,
        'field-type': ExtractionFieldType.String,
        'field-name': snakeCase(data.fieldName),
        'extractor-type': data.extractorType,
        'preset-extractor': data.presetExtractor,
      };
    case ExtractorType.InquiryFieldComparison:
      assertNever(ExtractorType as never);
      break;
    case ExtractorType.TransactionFieldComparison:
      assertNever(ExtractorType as never);
      break;
    default: {
      return {
        ...(keepOldExtractionRequest ? oldExtractionRequest : {}),
        version: '2.0',
        'extraction-type': ExtractionType.Unguided,
        'field-type': ExtractionFieldType.String,
        'field-name': snakeCase(data.fieldName),
        'extractor-type': data.extractorType,
      };
    }
  }
};

export const validateUnguidedExtractionPanelForm = (
  data: ExtractionPanelForm,
  usedFieldNames: string[],
) => {
  const errors: FieldErrors<ExtractionPanelForm> = {};
  const errorRequired = { type: 'required', message: 'Required' };

  if (usedFieldNames.includes(snakeCase(data?.fieldName))) {
    errors.fieldName = {
      type: 'conflict',
      message: 'An extraction with this label already exists. Please choose a different label.',
    };
  }
  if (data?.extractorType == null) {
    errors.extractorType = errorRequired;
  }
  switch (data?.extractorType) {
    case ExtractorType.Ai: {
      if (data?.extractionPreference == null) {
        errors.extractionPreference = errorRequired;
      }
      if (data?.additionalInstructions?.length > 200) {
        errors.additionalInstructions = {
          type: 'maxLength',
          message: 'Maximum of 200 characters',
        };
      }
      break;
    }
    case ExtractorType.Text:
      if (data?.textExtractors == null || data?.textExtractors?.length === 0) {
        errors.textExtractors = {
          type: 'required',
          message: 'At least one extractor is required',
        };
      } else {
        const textExtractorErrors = data?.textExtractors?.map((extractor) => {
          const textExtractorError: {
            [K in keyof TextExtractor]?: { type: string; message: string };
          } = {};
          if (extractor['extract-pattern'] == null || extractor['extract-pattern'] === '') {
            textExtractorError['extract-pattern'] = errorRequired;
          }
          if (extractor['edit-distance-maximum'] == null) {
            textExtractorError['edit-distance-maximum'] = errorRequired;
          } else if (!Number.isInteger(extractor['edit-distance-maximum'])) {
            textExtractorError['edit-distance-maximum'] = {
              type: 'integer',
              message: 'Must be an integer',
            };
          } else if (extractor['edit-distance-maximum'] < 0) {
            textExtractorError['edit-distance-maximum'] = {
              type: 'min',
              message: 'Minimum of 0',
            };
          } else if (extractor['edit-distance-maximum'] > 3) {
            textExtractorError['edit-distance-maximum'] = {
              type: 'max',
              message: 'Maximum of 3',
            };
          }
          if (extractor['extract-action'] == null) {
            textExtractorError['extract-action'] = errorRequired;
          }
          if (Object.keys(textExtractorError).length > 0) {
            return textExtractorError;
          }
        });

        if (textExtractorErrors.some((error) => error)) {
          errors.textExtractors = textExtractorErrors;
        }
      }
      break;
    case ExtractorType.Regex:
      if (data?.regexExtractors == null || data?.regexExtractors?.length === 0) {
        errors.regexExtractors = {
          type: 'required',
          message: 'At least one extractor is required',
        };
      } else {
        const regexExtractorErrors = data?.regexExtractors?.map((extractor) => {
          const regexExtractorError: {
            [K in keyof RegexExtractor]?: { type: string; message: string };
          } = {};
          if (extractor['extract-pattern'] == null || extractor['extract-pattern'] === '') {
            regexExtractorError['extract-pattern'] = errorRequired;
          } else {
            try {
              new RegExp(extractor['extract-pattern']);
            } catch (e) {
              regexExtractorError['extract-pattern'] = {
                type: 'regex',
                message: 'Invalid regex pattern',
              };
            }
          }
          if (extractor['extract-action'] == null) {
            regexExtractorError['extract-action'] = errorRequired;
          }
          if (Object.keys(regexExtractorError).length > 0) {
            return regexExtractorError;
          }
        });
        if (regexExtractorErrors.some((error) => error)) {
          errors.regexExtractors = regexExtractorErrors;
        }
      }
      break;
    case ExtractorType.KeyValuePair:
      if (data?.searchKeys == null || data?.searchKeys.length === 0) {
        errors.searchKeys = errorRequired;
      } else {
        data?.searchKeys.some((searchKey) => {
          if (searchKey === '') {
            errors.searchKeys = {
              type: 'minLength',
              message: 'Extraction key cannot be empty',
            };
            return true;
          }
        });
      }
      break;
    case ExtractorType.Preset:
      if (data?.presetExtractor == null) {
        errors.presetExtractor = errorRequired;
      }
      break;
  }
  return errors;
};

interface ExtractorSectionProps {
  formReturn: UseFormReturn<ExtractionPanelForm, object>;
  disabled?: boolean;
}

const TextExtractorSection = ({ formReturn, disabled = false }: ExtractorSectionProps) => {
  const { register, formState, watch, clearErrors, getValues, control } = formReturn;
  const { errors } = formState;

  const { append, remove } = useFieldArray({ control, name: 'textExtractors' });

  const addTextExtractor = () => {
    if (!getValues('textExtractors')?.length && errors?.textExtractors) {
      clearErrors('textExtractors');
    }
    append({
      'extract-pattern': '',
      'edit-distance-maximum': 1,
      'extract-action': ExtractAction.Accept,
    });
  };

  const deleteTextExtractor = (index: number) => remove(index);

  return (
    <>
      <Paragraph>
        Provide literal strings that the extractor should search for in the document.
      </Paragraph>
      <Spacer height={1.5} />
      <ExtractionPanelListBox
        formReturn={formReturn}
        name='textExtractors'
        title='Text extractor'
        extractors={watch('textExtractors')}
        disabled={disabled}
        addExtractor={addTextExtractor}
        deleteExtractor={deleteTextExtractor}
        renderRow={(textExtractor: Partial<TextExtractor>, index: number) => (
          <ExtractorEntry>
            {textExtractor?.['extract-pattern'] && (
              <>
                {textExtractor?.['extract-action'] === ExtractAction.Accept && (
                  <CheckIcon color={Color.BorderIconSuccess} />
                )}
                {textExtractor?.['extract-action'] === ExtractAction.Reject && (
                  <XIcon color={Color.BorderIconDanger} />
                )}
                <Spacer height={0} width={1} />
              </>
            )}
            <Paragraph style={errors?.textExtractors?.[index] ? { color: Color.TextDanger } : {}}>
              {textExtractor?.['extract-pattern'] || 'Empty extractor'}
            </Paragraph>
          </ExtractorEntry>
        )}
        panel={(index: number) => (
          <>
            <StyledLabel>Text pattern</StyledLabel>
            <FieldWrapper>
              <Input
                {...register(`textExtractors.${index}.extract-pattern`)}
                placeholder='Enter text pattern'
                hasError={!!errors?.textExtractors?.[index]?.['extract-pattern']}
                autoFocus
              />
            </FieldWrapper>
            <FieldErrorText
              message={errors?.textExtractors?.[index]?.['extract-pattern']?.message}
            />
            <Spacer height={1.5} />
            <StyledLabel>Maximum edit distance</StyledLabel>
            <FieldWrapper>
              <Input
                {...register(`textExtractors.${index}.edit-distance-maximum`, {
                  valueAsNumber: true,
                })}
                placeholder='Enter maximum edit distance'
                type='number'
                hasError={!!errors?.textExtractors?.[index]?.['edit-distance-maximum']}
              />
            </FieldWrapper>
            <FieldErrorText
              message={errors?.textExtractors?.[index]?.['edit-distance-maximum']?.message}
            />
            <Spacer height={1.5} />
            <StyledLabel>If a match is found</StyledLabel>
            <FieldWrapper>
              <RadioGroup horizontal>
                {Object.values(ExtractAction).map((extractAction) => (
                  <Radio
                    key={extractAction}
                    {...register(`textExtractors.${index}.extract-action`)}
                    label={EXTRACT_ACTION_TO_DISPLAY_NAME[extractAction]}
                    value={extractAction}
                  />
                ))}
              </RadioGroup>
            </FieldWrapper>
            <FieldErrorText
              message={errors?.textExtractors?.[index]?.['extract-action']?.message}
            />
          </>
        )}
      />
      <FieldErrorText
        message={
          errors?.textExtractors?.message ||
          (errors?.textExtractors?.length ? 'One or more extractors are invalid' : '')
        }
      />
    </>
  );
};

const RegexExtractorSection = ({ formReturn, disabled = false }: ExtractorSectionProps) => {
  const { register, formState, watch, clearErrors, getValues, control } = formReturn;
  const { errors } = formState;

  const { append, remove } = useFieldArray({ control, name: 'regexExtractors' });

  const addRegexExtractor = () => {
    if (!getValues('regexExtractors')?.length && errors?.regexExtractors) {
      clearErrors('regexExtractors');
    }
    append({
      'extract-pattern': '',
      'extract-action': ExtractAction.Accept,
      'extract-name': '',
    });
  };

  const deleteRegexExtractor = (index: number) => remove(index);

  return (
    <>
      <Paragraph>
        Provide regex patterns that the extractor should use to search for matches in the document.
      </Paragraph>
      <Spacer height={1.5} />
      <ExtractionPanelListBox
        formReturn={formReturn}
        name='regexExtractors'
        title='Regex extractor'
        extractors={watch('regexExtractors')}
        disabled={disabled}
        addExtractor={addRegexExtractor}
        deleteExtractor={deleteRegexExtractor}
        renderRow={(regexExtractor: Partial<RegexExtractor>, index: number) => (
          <ExtractorEntry>
            {(regexExtractor?.['extract-name'] || regexExtractor?.['extract-pattern']) && (
              <>
                {regexExtractor?.['extract-action'] === ExtractAction.Accept && (
                  <CheckIcon color={Color.BorderIconSuccess} />
                )}
                {regexExtractor?.['extract-action'] === ExtractAction.Reject && (
                  <XIcon color={Color.BorderIconDanger} />
                )}
                <Spacer height={0} width={1} />
              </>
            )}
            <Paragraph style={errors?.regexExtractors?.[index] ? { color: Color.TextDanger } : {}}>
              {regexExtractor?.['extract-name'] ||
                regexExtractor?.['extract-pattern'] ||
                'Empty extractor'}
            </Paragraph>
          </ExtractorEntry>
        )}
        panel={(index: number) => (
          <>
            <StyledLabel>Regex label (optional)</StyledLabel>
            <FieldWrapper>
              <Input
                {...register(`regexExtractors.${index}.extract-name`)}
                placeholder='Enter regex label'
                hasError={!!errors?.regexExtractors?.[index]?.['extract-name']}
                autoFocus
              />
            </FieldWrapper>
            <FieldErrorText message={errors?.regexExtractors?.[index]?.['extract-name']?.message} />
            <Spacer height={1.5} />
            <StyledLabel>Regex pattern</StyledLabel>
            <FieldWrapper>
              <Input
                {...register(`regexExtractors.${index}.extract-pattern`)}
                placeholder='Enter regex pattern'
                hasError={!!errors?.regexExtractors?.[index]?.['extract-pattern']}
              />
            </FieldWrapper>
            <FieldErrorText
              message={errors?.regexExtractors?.[index]?.['extract-pattern']?.message}
            />
            <Spacer height={1.5} />
            <StyledLabel>If a match is found</StyledLabel>
            <FieldWrapper>
              <RadioGroup horizontal>
                {Object.values(ExtractAction).map((extractAction) => (
                  <Radio
                    key={extractAction}
                    {...register(`regexExtractors.${index}.extract-action`)}
                    label={EXTRACT_ACTION_TO_DISPLAY_NAME[extractAction]}
                    value={extractAction}
                  />
                ))}
              </RadioGroup>
            </FieldWrapper>
            <FieldErrorText
              message={errors?.regexExtractors?.[index]?.['extract-action']?.message}
            />
          </>
        )}
      />
      <FieldErrorText
        message={
          errors?.regexExtractors?.message ||
          (errors?.regexExtractors?.length ? 'One or more extractors are invalid' : '')
        }
      />
    </>
  );
};

const PresetExtractorSection = ({ formReturn, disabled = false }: ExtractorSectionProps) => {
  const { formState, control } = formReturn;
  const { errors } = formState;

  const formatPresetExtractorDisplayName = (presetExtractor: PresetExtractor) => {
    let displayName: string = PRESET_EXTRACTOR_TO_DISPLAY_NAME[presetExtractor];
    if (DEPRECATED_PRESET_EXTRACTORS.includes(presetExtractor)) {
      displayName += ' (Deprecated)';
    }
    return displayName;
  };

  return (
    <>
      <Paragraph>Select a pre-defined extractor.</Paragraph>
      <Spacer height={1.5} />
      <StyledLabel>Preset</StyledLabel>
      <Controller
        name='presetExtractor'
        control={control}
        render={({ field }) => {
          const options = Object.values(PresetExtractor).filter((presetExtractor) => {
            return (
              !DEPRECATED_PRESET_EXTRACTORS.includes(presetExtractor) ||
              presetExtractor === field.value
            );
          });

          return (
            <FieldWrapper>
              <Select
                {...field}
                placeholder='Select preset'
                hasError={!!errors.presetExtractor}
                disabled={disabled}
              >
                {options.map((presetExtractor) => (
                  <Option key={presetExtractor} value={presetExtractor}>
                    {formatPresetExtractorDisplayName(presetExtractor)}
                  </Option>
                ))}
              </Select>
            </FieldWrapper>
          );
        }}
      />
      <FieldErrorText message={errors?.presetExtractor?.message} />
    </>
  );
};

const KeyValuePairExtractorSection = ({ formReturn, disabled = false }: ExtractorSectionProps) => {
  const { formState, control } = formReturn;
  const { errors } = formState;

  return (
    <>
      <Paragraph>
        Enter extraction keys for the extractor to find keyed information on the document.
      </Paragraph>
      <Spacer height={1.5} />
      <Tooltip
        side='right'
        trigger={
          <StyledLabel>
            <TooltipIndicatorWrapper>Extraction keys</TooltipIndicatorWrapper>
          </StyledLabel>
        }
      >
        <TooltipParagraph>
          Extraction keys are case insensitive. Otherwise, they should be written exactly as you
          would expect them to appear on the documents.
        </TooltipParagraph>
      </Tooltip>
      <Controller
        name='searchKeys'
        control={control}
        render={({ field }) => (
          <FieldWrapper>
            <ReactCreatableSelect
              placeholder='Enter extraction keys'
              options={field.value?.map((extractor) => ({
                label: extractor,
                value: extractor,
              }))}
              noOptionsMessage={({ inputValue }) =>
                inputValue === '' ? null : `Duplicate key "${inputValue}" found`
              }
              isMulti
              components={{ DropdownIndicator: () => null }}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              inputRef={field.ref}
              hasError={!!errors?.searchKeys}
              disabled={disabled}
            />
          </FieldWrapper>
        )}
      />
      <FieldErrorText message={errors?.searchKeys?.message} />
    </>
  );
};

const AiExtractorSection = ({ formReturn }: ExtractorSectionProps) => {
  const { register, formState, control } = formReturn;
  const { errors } = formState;

  return (
    <>
      <StyledLabel>Extraction preference</StyledLabel>
      <Controller
        name='extractionPreference'
        control={control}
        defaultValue={UnguidedAiExtractionPreference.BestMatch}
        render={({ field }) => (
          <FieldWrapper>
            <Select
              {...field}
              placeholder='Select extraction preference'
              hasError={!!errors?.extractionPreference}
            >
              {Object.values(UnguidedAiExtractionPreference).map((extractionPreference) => (
                <Option
                  key={extractionPreference}
                  value={extractionPreference}
                  subtitle={EXTRACTION_PREFERENCE_TO_DESCRIPTION[extractionPreference]}
                >
                  {startCase(extractionPreference)}
                </Option>
              ))}
            </Select>
          </FieldWrapper>
        )}
      />
      <FieldErrorText message={errors?.extractionPreference?.message} />
      <Spacer height={1.5} />
      <StyledLabel>Additional instructions (optional)</StyledLabel>
      <FieldWrapper>
        <TextArea
          {...register('additionalInstructions')}
          style={{ resize: 'vertical' }}
          placeholder='Enter additional instructions'
          hasError={!!errors?.additionalInstructions}
        />
      </FieldWrapper>
      <FieldErrorText message={errors?.additionalInstructions?.message} />
    </>
  );
};

const UnguidedExtractionPanel = ({ formReturn, disabled = false }: ExtractorSectionProps) => {
  const { watch } = formReturn;

  const extractorType = watch('extractorType');

  switch (extractorType) {
    case ExtractorType.Ai:
      return <AiExtractorSection formReturn={formReturn} disabled={disabled} />;
    case ExtractorType.Text:
      return <TextExtractorSection formReturn={formReturn} disabled={disabled} />;
    case ExtractorType.Regex:
      return <RegexExtractorSection formReturn={formReturn} disabled={disabled} />;
    case ExtractorType.KeyValuePair:
      return <KeyValuePairExtractorSection formReturn={formReturn} disabled={disabled} />;
    case ExtractorType.Preset:
      return <PresetExtractorSection formReturn={formReturn} disabled={disabled} />;
    default:
      return null;
  }
};

export default UnguidedExtractionPanel;
